import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import QuestionReviewCard from './QuestionReviewCard';
import './lessonFinish.css';

export default function LessonFinish({
    isOpen = false,
    lessonName = '',
    questions = [],
    exam = {},
    onClose,
    onFinish,
    onOpenQuestion
}) {
    const { t } = useTranslation('common');
    const [filterTab, setFilterTab] = useState('all'); // 'all' | 'unanswered' | 'answered'

    // Filter out type 11 questions (text/media non-questions)
    const validQuestions = useMemo(() => {
        const qs = Array.isArray(questions) ? questions : [];
        return qs.filter(q => {
            if (!q) return false;
            const typeId = q.Type?.Id ?? q.Type ?? q.TypeId;
            return Number(typeId) !== 11;
        });
    }, [questions]);

    // Check if a question has been answered
    const isQuestionAnswered = useCallback((question) => {
        if (!question) return false;
        const marksList = Array.isArray(exam?.marks) ? exam.marks : [];
        const answersList = Array.isArray(exam?.current) ? exam.current : [];

        const hasMark = marksList.some(p => p && p.Id === question.Id);
        if (hasMark) return true;

        const ans = answersList.find(p => p && p.Id === question.Id);
        if (ans && ans.answers !== undefined && ans.answers !== null) {
            if (Array.isArray(ans.answers)) return ans.answers.length > 0;
            return true;
        }
        return false;
    }, [exam]);

    const totalCount = validQuestions.length;
    const answeredCount = useMemo(() => {
        return validQuestions.filter(isQuestionAnswered).length;
    }, [validQuestions, isQuestionAnswered]);

    const unansweredCount = Math.max(0, totalCount - answeredCount);
    const progressPct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;

    // Filtered list based on active tab
    const displayQuestions = useMemo(() => {
        if (filterTab === 'answered') {
            return validQuestions.filter(isQuestionAnswered);
        }
        if (filterTab === 'unanswered') {
            return validQuestions.filter(q => !isQuestionAnswered(q));
        }
        return validQuestions;
    }, [validQuestions, filterTab, isQuestionAnswered]);

    if (!isOpen) return null;

    return (
        <section className={`lesson-finish-panel ${isOpen ? 'show' : ''}`} role="dialog" aria-modal="true">
            <div className="lesson-finish-container">
                {/* 1. Header */}
                <div className="lesson-finish-header">
                    <div className="lesson-finish-title-group">
                        <h2>
                            <i className="fas fa-clipboard-check text-primary"></i>
                            <span>{lessonName || t('Pages.Lessons.Finish.title') || t('Pages.Lessons.End') || 'مراجعة وإنهاء الدرس'}</span>
                        </h2>
                        <p className="lesson-finish-subtitle">
                            {t('Pages.Lessons.Finish.subtitle') || t('Pages.Exams.EndExamSubTitle') || 'راجع إجاباتك على الأسئلة قبل تسليم الدرس والاطلاع على التقرير الشامل'}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="lesson-finish-close-btn"
                        onClick={onClose}
                        title={t('Pages.Exams.Close') || 'إغلاق ومتابعة الدرس'}
                        aria-label="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* 2. KPI Summary Bar (Uniform Card Styles) */}
                <div className="lesson-finish-stats">
                    <div className="finish-stat-card total">
                        <div className="finish-stat-info">
                            <span className="finish-stat-label">
                                {t('Pages.Lessons.Finish.totalQuestions') || 'إجمالي الأسئلة'}
                            </span>
                            <span className="finish-stat-value">{totalCount}</span>
                        </div>
                        <div className="finish-stat-icon">
                            <i className="fas fa-layer-group"></i>
                        </div>
                    </div>

                    <div className="finish-stat-card answered">
                        <div className="finish-stat-info">
                            <span className="finish-stat-label">
                                {t('Pages.Lessons.Finish.answeredQuestions') || 'الأسئلة المحلولة'}
                            </span>
                            <span className="finish-stat-value text-success">{answeredCount}</span>
                        </div>
                        <div className="finish-stat-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                    </div>

                    <div className="finish-stat-card unanswered">
                        <div className="finish-stat-info">
                            <span className="finish-stat-label">
                                {t('Pages.Lessons.Finish.unansweredQuestions') || 'أسئلة غير مجابة'}
                            </span>
                            <span className={`finish-stat-value ${unansweredCount > 0 ? 'text-danger' : 'text-muted'}`}>
                                {unansweredCount}
                            </span>
                        </div>
                        <div className="finish-stat-icon">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                    </div>

                    <div className="finish-stat-card progress-card">
                        <div className="finish-stat-info">
                            <span className="finish-stat-label">
                                {t('Pages.Lessons.Finish.progressRate') || 'نسبة الإنجاز'}
                            </span>
                            <span className="finish-stat-value" style={{ color: '#7c3aed' }}>
                                {progressPct}%
                            </span>
                        </div>
                        <div className="finish-stat-icon">
                            <i className="fas fa-chart-line"></i>
                        </div>
                    </div>
                </div>

                {/* 3. Filter Tabs (Auto Width) */}
                <div className="lesson-finish-filter-bar">
                    <div className="finish-tabs">
                        <button
                            type="button"
                            className={`finish-tab-btn ${filterTab === 'all' ? 'active' : ''}`}
                            onClick={() => setFilterTab('all')}
                        >
                            <span>{t('Pages.Lessons.Finish.allQuestions') || 'جميع الأسئلة'}</span>
                            <span className="finish-tab-badge">{totalCount}</span>
                        </button>

                        <button
                            type="button"
                            className={`finish-tab-btn ${filterTab === 'unanswered' ? 'active' : ''}`}
                            onClick={() => setFilterTab('unanswered')}
                        >
                            <i className="fas fa-exclamation-circle text-danger ml-1"></i>
                            <span>{t('Pages.Lessons.Finish.unansweredOnly') || 'غير مجابة'}</span>
                            <span className="finish-tab-badge">{unansweredCount}</span>
                        </button>

                        <button
                            type="button"
                            className={`finish-tab-btn ${filterTab === 'answered' ? 'active' : ''}`}
                            onClick={() => setFilterTab('answered')}
                        >
                            <i className="fas fa-check-circle text-success ml-1"></i>
                            <span>{t('Pages.Lessons.Finish.answeredOnly') || 'تمت الإجابة'}</span>
                            <span className="finish-tab-badge">{answeredCount}</span>
                        </button>
                    </div>

                    <div className="text-muted small">
                        <i className="fas fa-info-circle ml-1 text-info"></i>
                        {t('Pages.Lessons.Finish.clickToReview') || 'انقر على أي سؤال لفتحه وحلّه أو تعديل إجابته'}
                    </div>
                </div>

                {/* 4. Questions Grid Body */}
                <div className="lesson-finish-body">
                    {displayQuestions.length > 0 ? (
                        <div className="finish-questions-grid">
                            {displayQuestions.map((q, idx) => {
                                const originalIndex = validQuestions.findIndex(item => item.Id === q.Id);
                                const hasAns = isQuestionAnswered(q);
                                return (
                                    <QuestionReviewCard
                                        key={`finish-q-${q.Id || idx}`}
                                        question={q}
                                        index={originalIndex >= 0 ? originalIndex : idx}
                                        hasAnswer={hasAns}
                                        onOpen={onOpenQuestion}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center p-5 text-center bg-white rounded border">
                            <i className="fas fa-check-double text-success mb-3" style={{ fontSize: '2.5rem' }}></i>
                            <h5 className="font-weight-bold text-dark mb-1">
                                {filterTab === 'unanswered'
                                    ? (t('Pages.Lessons.Finish.allAnsweredSuccess') || 'رائع! لقد أجبت على جميع الأسئلة')
                                    : 'لا توجد أسئلة في هذا التبويب'}
                            </h5>
                            <p className="text-muted small mb-0">
                                {filterTab === 'unanswered'
                                    ? 'يمكنك الآن تسليم الدرس بكل ثقة للاطلاع على تقرير النتيجة.'
                                    : 'اختر تبويباً آخر لاستعراض الأسئلة.'}
                            </p>
                        </div>
                    )}
                </div>

                {/* 5. Footer & Actions */}
                <div className="lesson-finish-footer">
                    {unansweredCount > 0 ? (
                        <div className="finish-warning-alert">
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>
                                {t('Pages.Lessons.Finish.warningUnanswered') || 'تنبيه: يوجد لديك'} <strong>{unansweredCount}</strong> {t('Pages.Lessons.Finish.unansweredQuestions') || 'أسئلة لم تقم بالإجابة عليها بعد.'}
                            </span>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center text-success font-weight-bold small" style={{ gap: '0.35rem' }}>
                            <i className="fas fa-check-circle"></i>
                            <span>{t('Pages.Lessons.Finish.allAnsweredSuccess') || 'تمت الإجابة على كافة الأسئلة بنجاح.'}</span>
                        </div>
                    )}

                    <div className="finish-actions-group">
                        <button
                            type="button"
                            className="finish-btn finish-btn-secondary"
                            onClick={onClose}
                        >
                            <i className="fas fa-arrow-right ml-1"></i>
                            <span>{t('Pages.Lessons.Finish.resumeLesson') || t('Pages.Lessons.Continue') || 'العودة ومتابعة الحل'}</span>
                        </button>

                        <div
                            role="button"
                            tabIndex={0}
                            className="theme-btn btn-style-two d-inline-flex align-items-center mb-0"
                            onClick={onFinish}
                        >
                            <span className="txt pl-2">
                                {t('Pages.Lessons.Finish.submitLesson') || t('Pages.Lessons.End') || 'إنهاء الدرس'}
                            </span>
                            <div className="mouse-btn-down">
                                <div className="chevron"></div>
                                <div className="chevron"></div>
                                <div className="chevron"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
